/**
 * The only place this frontend talks to faion-net-be.
 *
 * Two things are deliberately NOT built here, because they already exist and building
 * them again is how two sources of truth start:
 *
 *   - **Accounts and sessions** live in the backend's `users` app. This calls
 *     `auth/login`, `auth/refresh` and `auth/logout`; it does not hold a user table.
 *   - **Entitlement** lives in the backend's `licenses` app, whose tiers are already
 *     free / solo / pro / geek. This calls `licenses/me` and `licenses/validate`; it does
 *     not decide what a tier may do.
 *
 * Endpoint paths were read from the backend rather than assumed
 * (`apps/users/urls.py`, `apps/licenses/urls.py`). If they move, they move there first
 * and this file follows — never the reverse.
 */

// Two prefixes, because the backend has two. Auth lives under `api/v1/cli/` and licences
// are mounted at the v1 root — both read from `api/v1/urls.py` rather than assumed, after
// guessing wrong twice and getting 404s that looked like "no licence on this account".
export const API_BASE =
  import.meta.env.VITE_FAION_API ?? "https://api.faion.net/api/v1"

export type Tier = "free" | "solo" | "pro" | "geek"

/** What `cli/account/` returns — verified against `apps/cli/content_views.py`. */
export interface Account {
  email: string
  tier: Tier
  subscription_id: string | null
  token_expires_at: string | null
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

/**
 * Where the tokens live, and why.
 *
 * The backend returns JWTs in the response body rather than setting an httpOnly cookie
 * (verified against `apps/cli/auth_views.py`), so this is the frontend's decision to make
 * and it is a real trade rather than an obvious one:
 *
 *   - **The access token stays in memory.** It is the one that grants access right now, so
 *     it is never written anywhere a script or another tab can read.
 *   - **The refresh token goes in `sessionStorage`.** That survives a reload, which a
 *     usable sign-in requires, and dies when the tab closes. It is deliberately NOT
 *     `localStorage`: that persists indefinitely, across tabs and restarts, and turns one
 *     injected script into a permanent session.
 *
 * This is weaker than an httpOnly cookie the backend controls, and that remains the right
 * long-term answer — recorded rather than left implicit, because "we'll harden it later"
 * is not a plan unless somebody wrote down what "it" is.
 */
const REFRESH_KEY = "faion.refresh"

let _token: string | null = null
const token = () => _token

const storedRefresh = () => {
  try { return sessionStorage.getItem(REFRESH_KEY) } catch { return null }
}
const storeRefresh = (v: string | null) => {
  try { v ? sessionStorage.setItem(REFRESH_KEY, v) : sessionStorage.removeItem(REFRESH_KEY) }
  catch { /* private mode, or storage disabled — sign-in still works for this page */ }
}

export const signedIn = () => _token !== null

/** The relay needs the same token to identify the account. Reading it is fine;
 *  it stays in memory either way. */
export const accessToken = () => token()

/**
 * What `cli/auth/login/` and `cli/auth/refresh/` actually return — checked against a live
 * response, not read off a sibling app. `apps/users/` nests its tokens under `tokens.*`;
 * `apps/cli/` returns them flat, and this frontend talks to the CLI surface. Guessing that
 * they matched cost two rounds of "authentication credentials were not provided".
 */
interface TokenPayload {
  access_token?: string
  refresh_token?: string
  expires_in?: number
  tier?: Tier
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) ?? {}),
  }
  if (_token) headers.Authorization = `Bearer ${_token}`

    // Callers pass the full path below `api/v1` — `cli/auth/login/` for auth, `licenses/me`
  // for entitlement — because the backend mounts those at different depths.
  const res = await fetch(`${API_BASE}/${path.replace(/^\//, "")}`, { ...init, headers })
  if (!res.ok) {
    // Carry the backend's own message when it sends one: "wrong password" is actionable
    // and "500" is not, and a login form that hides the difference teaches people the
    // product is broken.
    let detail = `${res.status}`
    try {
      const body = await res.json()
      detail = body.detail ?? body.title ?? body.message ?? detail
    } catch {
      /* not JSON — the status is all there is */
    }
    throw new ApiError(res.status, detail)
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T)
}

export async function login(email: string, password: string): Promise<void> {
  const body = await call<TokenPayload>("cli/auth/login/", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  })
  if (!body.access_token) {
    throw new ApiError(500, "the backend signed us in but returned no token")
  }
  _token = body.access_token
  storeRefresh(body.refresh_token ?? null)
}

/**
 * Restore a session after a reload, using the stored refresh token.
 *
 * Returns false rather than throwing when there is nothing to restore or the token has
 * expired — "not signed in" is an ordinary state on first visit, not an error, and
 * treating it as one puts a red message in front of somebody who has done nothing wrong.
 */
let _restoring: Promise<boolean> | null = null

export function restore(): Promise<boolean> {
  // Single-flight, and it is not an optimisation. A refresh token is single-use: the
  // backend blocklists the old id before issuing the next pair. Two concurrent restores
  // therefore spend the same token, one of them gets a `401`, and its `catch` clears the
  // *good* token the other just stored — so a double call does not merely fail, it signs
  // the person out. React mounts effects twice in development, which is how this was
  // found; anything else that calls twice would do the same in production.
  if (!_restoring) _restoring = doRestore().finally(() => { _restoring = null })
  return _restoring
}

async function doRestore(): Promise<boolean> {
  const refresh = storedRefresh()
  if (!refresh) return false
  try {
    const body = await call<TokenPayload>("cli/auth/refresh/", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refresh }),
    })
    if (!body.access_token) return false
    _token = body.access_token
    // The backend rotates refresh tokens and blocklists the old JTI, so the replacement
    // must be stored or the next reload silently fails.
    if (body.refresh_token) storeRefresh(body.refresh_token)
    return true
  } catch {
    storeRefresh(null)
    return false
  }
}

export async function logout(): Promise<void> {
  const refresh = storedRefresh()
  try {
    await call<void>("cli/auth/logout/", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refresh }),
    })
  } catch {
    /* the server may already have invalidated it; local state is cleared regardless */
  } finally {
    // Cleared whatever the server said. A logout that leaves the token behind because the
    // network hiccupped is exactly the one that matters.
    _token = null
    storeRefresh(null)
  }
}

/**
 * The signed-in person, or null.
 *
 * One request, not two: `cli/account/` already returns the tier, so asking `licenses/me`
 * as well would be a second round trip to learn something we were just told — and a second
 * place for the two answers to disagree.
 */
export async function account(): Promise<Account | null> {
  try {
    return await call<Account>("cli/account/")
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null
    throw e
  }
}

/**
 * Whether this installation may use the product.
 *
 * Deliberately fails **open on a network error and closed on a refusal**: a backend that
 * cannot be reached must not lock somebody out of documents they already have — the
 * client runs on their machine and their content is in their own repository, so an
 * outage is our problem and should not become theirs. A backend that answers "no" is a
 * different thing entirely, and is honoured.
 */
export async function entitled(): Promise<{ allowed: boolean; reason: string }> {
  if (!signedIn()) return { allowed: false, reason: "not signed in" }
  try {
    const me = await account()
    if (!me) return { allowed: false, reason: "session expired" }
    return { allowed: true, reason: `${me.email} · ${me.tier}` }
  } catch (e) {
    if (e instanceof ApiError) return { allowed: false, reason: e.message }
    return { allowed: true, reason: "backend unreachable — failing open" }
  }
}
